import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { type FC, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { getCookie } from "react-use-cookie"

import { getPasswordValidationError, passwordRequirementsText } from "@forento/core/utilities/password"
import { toNullIfEmpty } from "@forento/core/utilities/string"
import Form from "@forento/ui/components/Form"

import { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useDisableGoogleSignIn } from "~/contexts/GoogleSignInContext"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import { Content, ErrorText, Inputs, InputsRow, NavigationButton, NavigationText, Text, Title } from "../Layout"
import { signup } from "."

const PlatformInvite: FC<{ token: string }> = ({ token }) => {
	const user = useUser()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	useDisableGoogleSignIn()

	const turnstileRef = useRef<TurnstileInstance | null>(null)

	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [password, setPassword] = useState("")
	const [passwordConfirmation, setPasswordConfirmation] = useState("")

	const [error, setError] = useState<string>()
	const [isSubmitting, setSubmitting] = useState(false)

	const invite = query.user.verifyCreatorInviteToken.useQuery(token)

	const handleSignup = async () => {
		if (token === null) return

		const passwordError = getPasswordValidationError(password)
		if (passwordError !== null) {
			setError(passwordError)
			return
		}

		const captchaToken = turnstileRef.current?.getResponse()
		if (!captchaToken) {
			setError("Please complete the captcha verification.")
			return
		}

		setSubmitting(true)
		const result = await signup({
			firstName,
			lastName,
			password,
			passwordConfirmation,
			inviteToken: token,
			source: searchParams.get("s") ?? toNullIfEmpty(getCookie("s")) ?? undefined,
			captchaToken,
		})
		setSubmitting(false)
		if (result.status !== "success") {
			setError(result.error)
			turnstileRef.current?.reset()
			return
		}

		await user.reload({ clearSessionCache: true })

		navigate(routes.main.index())
	}

	if (invite.error) {
		return <Text>Failed to load invite. Please try again later.</Text>
	}
	if (invite.data === undefined) {
		return <PartialLoadingPage />
	}
	if (invite.data.status === "invalid") {
		return (
			<Text>Your academy invite is unfortunately invalid. Make sure you have entered the full URL address.</Text>
		)
	} else if (invite.data.status === "expired") {
		return (
			<Text>
				Your academy invite has unfortunately expired. You need to ask the academy admin to invite you again.
			</Text>
		)
	}

	return (
		<Content as={Form} onSubmit={handleSignup}>
			<Title>Register a new account</Title>
			{error !== undefined && <ErrorText>{error}</ErrorText>}
			<Text>
				You've been invited to be part of <strong>{invite.data.invite.platform.name}</strong>.
			</Text>
			<Inputs>
				<InputsRow>
					<InputField
						label="First name"
						value={firstName}
						onChange={setFirstName}
						autoComplete="given-name"
					/>

					<InputField label="Last name" value={lastName} onChange={setLastName} autoComplete="family-name" />
				</InputsRow>

				<InputField
					inputType="email"
					label="Email"
					value={invite.data.invite.email}
					autoComplete="email"
					disabled
				/>

				<InputField
					inputType="password"
					label="Password"
					value={password}
					onChange={setPassword}
					autoComplete="new-password"
				/>
				<p className="text-left text-xs leading-relaxed text-zinc-500">{passwordRequirementsText}</p>

				<InputField
					inputType="password"
					label="Confirm password"
					value={passwordConfirmation}
					onChange={setPasswordConfirmation}
					autoComplete="new-password"
				/>
			</Inputs>

			<Turnstile ref={turnstileRef} siteKey={CONFIG.turnstileSiteKey} className="flex justify-center" />

			<NavigationText>
				By signing up you agree to our{" "}
				<NavigationButton onClick="https://forento.io/policy/terms-of-service" newTab>
					Terms of Service
				</NavigationButton>
				.
			</NavigationText>

			<SubmitButton variant="primary" isLoading={isSubmitting}>
				Sign up
			</SubmitButton>
			<NavigationText>
				Already have an account? <NavigationButton onClick={routes.account.signin()}>Sign in</NavigationButton>
			</NavigationText>
		</Content>
	)
}

export default PlatformInvite
